import type { InvoiceTaxCategoryCode, InvoiceTaxCategoryKey } from '../utils/taxCategoryCatalog';
import type { BundeslandCode } from '../utils/ustvaExport';

// Interface für rechtliche Zustimmungen (DSGVO-konform)
export interface LegalAgreement {
  accepted: boolean;
  acceptedAt: string; // ISO-Timestamp
  acceptedByIp?: string | null; // IP-Adresse für rechtlichen Nachweis
  documentVersion: string; // z.B. "1.0"
  documentHash?: string | null; // Optional: SHA-256 Hash der PDF
}

// Interface für alle rechtlichen Vereinbarungen
export interface LegalAgreements {
  avv?: LegalAgreement; // Auftragsverarbeitungsvereinbarung (Signup)
  agb?: LegalAgreement; // Allgemeine Geschäftsbedingungen
  datenschutz?: LegalAgreement; // Datenschutzerklärung
  avvInbound?: LegalAgreement; // Zusätzliche AVV für das Empfangspostfach (Loop A, §7.4)
}

export interface Contact {
  name: string | null;
  department: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
}

export interface Communication {
  uriScheme: string;
  uriId: string;
}

export interface BankDetails {
  iban: string;
  bic?: string; // Optional
  bankName?: string; // Optional
  accountHolder?: string; // NEU: Kontoinhaber (wichtig für XRechnung BT-85)
}

export interface Seller {
  name: string;
  id: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  email?: string | null; // NEU: Direkte E-Mail des Verkäufers
  phone?: string | null; // NEU: Direkte Telefonnummer des Verkäufers
  authEmail?: string | null; // Login-Email (automatisch vom Auth-System gespeichert)
  lastLoginAt?: Date | null; // Letzter Login-Zeitpunkt
  vatNumber: string | null;
  taxNumber: string | null;
  street2: string | null;
  globalId: string | null;
  globalIdScheme: string | null;
  // NEU: Handelsregister-Pflichtangaben
  commercialRegisterNumber: string | null; // Handelsregisternummer (z.B. HRB 12345)
  commercialRegisterLocation: string | null; // Ort des Handelsregisters (z.B. München)
  contact: Contact;
  communication: Communication[];
  bankDetails?: BankDetails;
  logoUrl?: string | null; // URL to logo in Firebase Storage (AP4)
  isPremium?: boolean; // NEU: Für AP6/AP7
  /**
   * Anzahl der im laufenden Kalendermonat erstellten Rechnungen (Free-Tier-Zähler).
   * READ-ONLY im Frontend — ausschließlich PHP-Backend-verwaltet (atomarer
   * Inkrement pro erstellter Rechnung, monatlicher Reset via
   * `resetMonthlyInvoiceCount`-Scheduled-Function). `saveSellerData()` strippt
   * dieses Feld hart (`PROTECTED_BACKEND_FIELDS`, AuthContext.tsx) — es wird
   * hier nur GELESEN (M-UX-G4 Verbrauchszähler, `InvoiceUsageMeter`).
   */
  monthlyInvoiceCount?: number;
  /** API-Zugang für Server-to-Server Rechnungs-API. Gesetzt ausschließlich via Firestore-Console durch Admin. */
  apiAccess?: boolean;
  /**
   * Loop-B Kanzlei-Kanal Beta-Flag (WL-K0c). Backend-only, wie `isPremium`:
   * gesetzt ausschließlich serverseitig durch die Contagion-Writer/das Backfill
   * (`functions/src/access/loopBBetaAccess.ts`). `saveSellerData` strippt es,
   * Firestore-Rules verbieten den Client-Write. Der FE-Resolver `isLoopBBeta`
   * liest es rein kosmetisch. */
  loopBBeta?: boolean;

  /**
   * W3 Self-Service-Kanzlei-Kennzeichnung (M-OB-11, §8.1). Reines UX-Signal —
   * steuert nur FE-Sichtbarkeit (Nav, AdvisorLandingGate, Mandanten-Stepper),
   * ist KEINE Autorisierungsquelle. Bewusst client-schreibbar: `saveSellerData`
   * strippt es NICHT (nicht in `PROTECTED_BACKEND_FIELDS`), Firestore-Rules
   * verbieten es nicht. Gesetzt einmalig von `AuthContext` nach dem ersten
   * sellerData-Load, wenn der Registrierungs-Marker (`pendingAccountType`)
   * gewählt wurde. `absent` ⇒ normaler Owner. */
  accountType?: 'kanzlei';

  /**
   * BR-GA (jurist-bank-haftung.md §5/§7.2, Auflage B): server-persistierter
   * Bank-Abgleich-AVV-Consent. `bankAvvAcceptedVersion` trägt die zugestimmte
   * AVV-Version (== `BANK_AVV_VERSION`), `bankAvvAcceptedAt` den ISO-8601-
   * Zeitstempel der Zustimmung. Bewusst client-schreibbar: `saveSellerData`
   * strippt die Felder NICHT (nicht in `PROTECTED_BACKEND_FIELDS`), die
   * Firestore-Rules erlauben dem Owner den Write auf sein eigenes
   * `sellers/{uid}`-Dokument. `absent` ⇒ noch nicht zugestimmt ⇒ Import gesperrt
   * (fail-closed, s. `hasBankAvvConsent`).
   */
  bankAvvAcceptedVersion?: string;
  bankAvvAcceptedAt?: string;

  /**
   * M13a-GA (Belegscanner-AVV, USA-Drittlandtransfer via Anthropic):
   * server-persistierte Zustimmung. `receiptScanAvvAcceptedVersion` trägt die
   * zugestimmte AVV-Version (== `RECEIPT_SCAN_AVV_VERSION`),
   * `receiptScanAvvAcceptedAt` den ISO-8601-Zeitstempel der Zustimmung. Bewusst
   * client-schreibbar: `saveSellerData` strippt die Felder NICHT (nicht in
   * `PROTECTED_BACKEND_FIELDS`), die Firestore-Rules erlauben dem Owner den Write
   * auf sein eigenes `sellers/{uid}`-Dokument. `absent` ⇒ noch nicht zugestimmt ⇒
   * Scan gesperrt (fail-closed, s. `hasReceiptScanAvvConsent`; Server spiegelt via
   * `avv_consent_required`).
   */
  receiptScanAvvAcceptedVersion?: string;
  receiptScanAvvAcceptedAt?: string;

  // Rechnungsnummern-Format-Felder
  invoiceNumberFormat?: string | null; // z.B. "REyyyy-MM-{number}"
  invoiceNumberStart?: number | null; // Startnummer, z.B. 227
  invoiceNumberCurrent?: number | null; // Aktuelle Nummer, wird automatisch inkrementiert
  
  // Kleinunternehmer-Status
  isSmallBusiness?: boolean; // § 19 UStG

  /**
   * Umsatzsteuer-Berechnungsart (§16 vs. §20 UStG) — M8 UStVA-Vorschau.
   * 'soll' = Soll-Versteuerung (Regelfall §16 Abs. 1): USt entsteht mit Ausführung
   *          der Leistung → Periodenkriterium = LEISTUNGSDATUM (BT-72 deliveryDate).
   * 'ist'  = Ist-Versteuerung (§20, nur auf Antrag, Vorjahresumsatz ≤ 800.000 €):
   *          USt entsteht mit Vereinnahmung → Periodenkriterium = ZAHLUNGSDATUM.
   * Default (fehlend) ⇒ 'soll' (fail-closed in den häufigeren gesetzlichen Regelfall).
   * Frontend-schreibbar via saveSellerData (NICHT in PROTECTED_BACKEND_FIELDS, wie isSmallBusiness).
   */
  taxationMode?: 'soll' | 'ist';

  /**
   * Bundesland des Finanzamts (16-Länder-Code, §5.3) — NEU für den UStVA-XML-Export
   * (M-U10). Additives OPTIONALES Feld: Bestands-Docs tragen es nicht; das Export-Panel
   * bietet bei Fehlen einen Inline-Select an und persistiert die Wahl via `saveSellerData`
   * (KEIN Protected Field — der Rules-Write-Guard betrifft nur Nummernkreis/Zähler).
   * Wird zusammen mit der `taxNumber` in das Bundesschema der Steuernummer umgesetzt.
   */
  bundesland?: BundeslandCode;

  // Stripe-Abonnement-Felder:
  subscriptionId?: string; // Stripe Subscription ID
  subscriptionStatus?: string; // active, trialing, past_due, canceled, etc.
  planName?: string; // Anzeigename des Plans, z.B. "Premium Monatlich"
  currentPeriodEnd?: any; // Firestore Timestamp für das Ende der aktuellen Periode
  cancelAtPeriodEnd?: boolean; // Ob das Abonnement am Ende der Periode gekündigt wird
  stripeCustomerId?: string; // Stripe Customer ID
  /**
   * Zahlungsausfall/Grace-Ende (architecture-past-due-reactivation.md §8.2, Rev. 2).
   * `gracePeriodEnd` ist das Ende der 14-tägigen Kulanzfrist nach dem ersten
   * `past_due`-Event (server-gesetzt vom Dunning-System); `dunningActive`
   * markiert, dass die letzte Abbuchung fehlgeschlagen ist und Premium nur
   * noch bis dahin gilt. Beide Felder sind additiv/optional und dienen im FE
   * ausschließlich der Anzeige des Dunning-Hinweises auf der Abo-Seite —
   * keine Entscheidung im Code hängt daran (Durchsetzung läuft serverseitig
   * gegen Stripe, B12). Client-beschreibbar (F3), Schreiben schadet nur der
   * eigenen Anzeige.
   */
  gracePeriodEnd?: { toDate: () => Date } | Date;
  dunningActive?: boolean;
  
  // Standardwerte für neue Rechnungen:
  defaultCurrency?: string | null; // Standard-Währung (z.B. EUR)
  defaultQuantityUnit?: string | null; // Standard-Einheit (UN/ECE Code, z.B. H87)
  defaultPaymentTerms?: string | null; // Standard-Zahlungsbedingungen
  defaultNotes?: string | null; // Standard-Notizen
  defaultUnitCode?: string | null; // Standard-Einheitencode (z.B. H87 für Stück)
  defaultTaxRate?: number | null; // Standard-Steuersatz (z.B. 19 für 19%)
  
  // Angebotsnummern-Format-Felder (NEU)
  offerNumberFormat?: string | null; // z.B. "ANyyyy-MM-{number:4}"
  offerNumberStart?: number | null; // Startnummer für Angebote
  offerNumberCurrent?: number | null; // Aktuelle Angebotsnummer
  
  // Rechtliche Zustimmungen (DSGVO-konform)
  legalAgreements?: LegalAgreements;
  
  // Marketing-Consent (DSGVO-konform)
  // "functions" = nur funktionale E-Mails (Features, Vertragsrelevantes) - Standard
  // "full" = Marketing + Angebote (erfordert DOI)
  // "none" = keine E-Mails
  marketingConsent?: 'functions' | 'full' | 'none';
  marketingConsentUpdatedAt?: string; // ISO-Timestamp der letzten Änderung
  marketingBannerDismissed?: boolean; // User hat "nicht mehr fragen" gewählt
  marketingDoiConfirmed?: boolean; // DOI für "full" wurde bestätigt
  marketingDoiConfirmedAt?: string; // Wann DOI bestätigt wurde
  
  // M11b Portal-Fix: von flagPendingAdvisorInvitesOnRegister gesetzt, wenn beim
  // Registrieren ein passender pending Token-Invite auf die E-Mail existiert. Optional
  // → keine Migration/Pflichtfeld-Kaskade. Signal (e) in useIsAdvisorContext.
  pendingAdvisorInviteHint?: boolean;

  // Onboarding-Status
  onboardingCompleted?: boolean; // Wurde das Onboarding-Interview abgeschlossen?
  onboardingCompletedAt?: string; // Wann wurde es abgeschlossen?
  onboardingProgress?: {
    currentStep: string;
    completedSteps: string[];
    startedAt: string;
    lastUpdatedAt: string;
    isCompleted: boolean;
    skippedSteps: string[];
  };
  showAppTour?: boolean; // Soll die App-Tour angezeigt werden?
  appTourCompleted?: boolean; // Wurde die App-Tour abgeschlossen?
  
  // UI-Spracheinstellung (ISO 639-1 Code: de, en, fr, pl, ro, ru)
  preferredLanguage?: string;

  // PDF-Einstellungen für individuelle Gestaltung
  pdfSettings?: PdfSettings;

  // DATEV-Export (M7) — optionale Stammdaten zur Header-Vorbelegung.
  // Additiv; vom Nutzer/Steuerberater pflegbar. Default-Kontenrahmen SKR03.
  datevSkr?: 'SKR03' | 'SKR04' | null; // gewählter Standardkontenrahmen
  datevBeraterNr?: string | null;      // DATEV-Berater-Nummer (leerbar, A7)
  datevMandantenNr?: string | null;    // DATEV-Mandanten-Nummer (leerbar, A7)
}

/**
 * PDF-Einstellungen für die individuelle Gestaltung von Rechnungen, Angeboten etc.
 * Alle Einstellungen sind optional - bei fehlenden Werten werden Defaults verwendet.
 */
export interface PdfSettings {
  // Farben
  accentColor?: string;           // Hex-Farbcode z.B. "#3498db" (Standard: Blau)
  
  // Absender- und Logo-Layout
  senderAddressLayout?: 'header-left' | 'above-recipient'; // Absender oben links oder als Rücksende-Zeile
  logoAlignmentHeader?: 'center' | 'right'; // Logo-Position wenn Absender oben links ist
  logoAlignmentAboveRecipient?: 'left' | 'center' | 'right'; // Logo-Position wenn Absender über Empfänger steht
  // Legacy (für Migration alter Daten)
  logoPosition?: 'left' | 'center' | 'right';
  logoSize?: 'small' | 'medium' | 'large' | 'custom';  // Standard: 'medium'
  logoHeightMm?: number;          // Benutzerdefinierte Höhe in mm (10-50), Standard: 20
  
  // Schriftart (mPDF-kompatibel)
  fontFamily?: 'arial' | 'helvetica' | 'times' | 'courier' | 'dejavusans' | 'dejavuserif';  // Standard: 'arial'
  
  // Tabellen-Stil
  tableStyle?: 'bordered' | 'minimal' | 'striped';  // Standard: 'bordered'
  
  // Header-Akzent-Linie
  headerAccent?: 'full' | 'thin' | 'none';  // Standard: 'full'
  
  // Footer-Einstellungen
  footerStyle?: 'colored' | 'simple' | 'minimal';  // Standard: 'colored'
  footerLayout?: 'single-line' | 'multi-line';     // Standard: 'single-line'
  contactNamePosition?: 'none' | 'footer' | 'meta'; // Standard: 'none' (nicht anzeigen)
  footerHideEmail?: boolean;      // Standard: false (E-Mail anzeigen)
  footerHidePhone?: boolean;      // Standard: false (Telefon anzeigen)
  footerHideWebsite?: boolean;    // Standard: false (Website anzeigen)
  
  // QR-Code
  showQrCode?: boolean;           // Standard: true
  // DIN 5008 / Layout
  addressWindowEnabled?: boolean; // Standard: false
  addressWindowLeftMm?: number;   // Standard: 20
  addressWindowTopMm?: number;    // Standard: 45
  lineSpacing?: 1 | 1.15 | 1.2 | 1.3; // Standard: 1.3
  pageMarginTopMm?: number;        // Standard: 0
  pageMarginRightMm?: number;      // Standard: 0
  pageMarginBottomMm?: number;     // Standard: 0
  pageMarginLeftMm?: number;       // Standard: 0
  
  // Zusätzliche Texte
  customFootnote?: string | null; // Benutzerdefinierte Fußnote unter dem Zahlungsbereich
  
  // Zahlenformat
  numberFormat?: 'de' | 'en' | 'auto';  // Standard: 'auto' (basierend auf Sprache)
  
  // Referenzzeilen anzeigen (Unser Zeichen / Ihr Zeichen)
  showReferenceLine?: boolean; // Standard: false
}

/**
 * Standard-PDF-Einstellungen (entsprechen dem aktuellen Design)
 * Diese werden verwendet, wenn keine benutzerdefinierten Einstellungen vorhanden sind.
 */
export const DEFAULT_PDF_SETTINGS: Required<Omit<PdfSettings, 'customFootnote'>> & { customFootnote: null } = {
  accentColor: '#3498db',
  senderAddressLayout: 'header-left',
  logoAlignmentHeader: 'right',
  logoAlignmentAboveRecipient: 'right',
  logoPosition: 'right',
  logoSize: 'medium',
  logoHeightMm: 20,
  fontFamily: 'arial',
  tableStyle: 'bordered',
  headerAccent: 'full',
  footerStyle: 'colored',
  footerLayout: 'single-line',
  contactNamePosition: 'none',
  footerHideEmail: false,
  footerHidePhone: false,
  footerHideWebsite: false,
  showQrCode: true,
  addressWindowEnabled: false,
  addressWindowLeftMm: 20,
  addressWindowTopMm: 45,
  lineSpacing: 1.3,
  pageMarginTopMm: 0,
  pageMarginRightMm: 0,
  pageMarginBottomMm: 0,
  pageMarginLeftMm: 0,
  customFootnote: null,
  numberFormat: 'auto',
  showReferenceLine: false,
};

// Interface für berechnete Gesamtwerte (kann im Frontend und Backend verwendet werden)
export interface CalculatedTotals {
  totalNet: number;
  totalTax: number;
  grandTotal: number;
  taxSummary: { [taxRate: string]: { base: number; amount: number } };
}

export interface Buyer {
  name: string; // Wird weiterhin für Kompatibilität genutzt, aber aus den neuen Feldern generiert
  companyName?: string | null; // NEU: Firmenname (optional)
  personName?: string | null;  // NEU: Vor-/Nachname (optional)
  street: string;
  zip: string;
  city: string;
  country: string;
  // Optional später:
  id?: string | null;
  street2?: string | null;
  reference?: string | null;
  communication?: Communication[];
  customerId?: string | null; // NEU: Echte Kundennummer
  // Neue Kommunikationsfelder
  email?: string | null;      // NEU: E-Mail-Adresse
  phone?: string | null;      // NEU: Telefonnummer
  website?: string | null;    // NEU: Website-URL
  // BT-48: USt-IdNr. des Käufers — Pflicht bei §6a IGL (K) / §13b (AE).
  // Additiv, optional; nur über den Steuerkategorie-Selektor (Flag) erfasst.
  vatNumber?: string | null;
}

export interface ShipToAddress {
  name: string;
  street: string;
  street2: string | null;
  street3: string | null;
  zip: string;
  city: string;
  country: string;
}

export interface ItemDiscount {
  type: 'percentage' | 'amount'; // prozentual oder Festbetrag
  value: number;                  // z.B. 10 für 10% oder 5.00 für €5
  reason?: string;                // optionale Beschreibung, z.B. "Mengenrabatt"
}

export interface DocumentAllowance {
  isCharge: boolean;   // false = Rabatt/Gutschrift, true = Aufschlag/Zuschlag
  type: 'percentage' | 'amount';
  value: number;
  taxRate: number;     // MwSt-Satz des Rabatts (EN16931 BT-96)
  reason?: string;     // BT-98 Freitext
  reasonCode?: string; // BT-97 aus UNCL 5189 (z.B. "95" für Rabatt)
}

export interface Skonto {
  percentage: number;  // z.B. 2.0 für 2%
  days: number;        // z.B. 10 für "innerhalb 10 Tage"
}

export interface InvoiceItem {
  id: string; // Temporäre ID für React-Key
  name: string;
  quantity: number;
  netPrice: number;
  unitPrice: number;
  taxRate: number;
  // Optionale Felder:
  description?: string | null;
  unitCode?: string; // z.B. H87
  sellerArticleId?: string | null;
  buyerArticleId?: string | null;
  globalId?: string | null;
  globalIdScheme?: string | null;
  grossPrice?: number | null;
  // Neue Felder für Produkt-Update-Handling
  productId?: string | null;        // Firestore Dokument-ID des Produkts
  itemAction?: 'update' | 'create' | 'none'; // Aktion pro Position
  // BT-148: Listenpreis VOR Rabatt (OHNE MwSt) — optional
  listPrice?: number | null;
  // BT-147: Positionsrabatt — optional
  discount?: ItemDiscount | null;
  // --- Steuerkategorie (VB-1, additiv, forward-only) ---
  // EN-16931 BT-151 Kategorie-Code (S|Z|E|AE|K|G), abgeleitet aus taxCategorySelection.
  // Feldname matcht den vom PHP/API-Pfad erwarteten Contract (ApiInvoiceMapperService).
  taxCategory?: InvoiceTaxCategoryCode | null;
  // Befreiungsgrund-Text BT-120/121, abgeleitet (Pflicht-Wortlaut je Kategorie).
  exemptionReason?: string | null;
  // UI-Auswahl-Schlüssel — verlustfreie fachliche Quelle (E ist mehrdeutig §19/§4).
  taxCategorySelection?: InvoiceTaxCategoryKey | null;
}

// Haupt-Interface für API-Daten
export interface InvoiceData {
  invoiceNumber?: string; // BT-1 Invoice number (now generated server-side)
  invoiceDate: string;   // BT-2 Invoice issue date (YYYY-MM-DD)
  currency: string;      // BT-5 Document currency code (ISO 4217)
  seller: Seller;        // BG-4 SELLER
  buyer: Buyer;          // BG-7 BUYER
  items: InvoiceItem[];  // BG-25 INVOICE LINE
  paymentTerms?: string | null; // BT-20 Payment terms description (optional)
  notes?: string | null;        // BT-22 Invoice note (optional)
  deliveryDate?: string | null; // BT-72 Actual delivery date (optional)
  shipTo?: ShipToAddress | null; // BG-15 SHIP TO PARTY (optional)
  // Additional optional fields (Advanced Mode)
  billingStartDate?: string | null; // BT-73 Invoicing period start date
  billingEndDate?: string | null;   // BT-74 Invoicing period end date
  tenderOrLotReference?: string | null; // BT-17 Tender or lot reference
  invoicedObjectId?: string | null;     // BT-18 Invoiced object identifier
  invoicedObjectScheme?: string | null; // BT-18 Scheme identifier
  contractReference?: string | null; // BT-12 Contract reference
  projectReferenceId?: string | null; // BT-11 Project reference ID
  projectReferenceName?: string | null;// BG-26 PROJECT (Name is not standard?) -> Use BT-11
  paymentDueDate?: string | null;   // BT-9 Payment due date
  paymentMandateReference?: string | null; // BT-89 Direct debit mandate reference
  buyerOrderReference?: string | null; // BT-13 Buyer reference (often PO number)
  sellerOrderReference?: string | null;// BT-14 Seller reference (often SO number)
  
  // Optionale, im Frontend berechnete Summen (zur Anzeige/Validierung)
  calculatedTotals?: CalculatedTotals;
  
  // Steuerung der Kundenaktualisierung (Frontend → Backend)
  customerAction?: 'update' | 'create' | 'none'; // Welche Aktion soll im Backend bzgl. des Kunden ausgeführt werden
  customerDocumentId?: string | null;            // Firestore‑Dokument‑ID des bestehenden Kunden (nur für update)
  
  // Steuerung der Produktaktualisierung (Frontend → Backend)
  productAction?: 'update' | 'create' | 'none';  // Generelle Aktion für alle Produkte

  // NEU: Workflow-Integration
  workflowReferenceId?: string;          // Verknüpfung zur übergeordneten Workflow-Referenz
  workflowStageId?: string;              // Verknüpfung zur entsprechenden Workflow-Etappe
  relatedOfferIds?: string[];            // Array mit IDs der zugehörigen Angebote
  
  // NEU: Versionsinformationen
  version?: {
    versionNumber: number;               // Version dieser Rechnung (für Revisionen)
    originalInvoiceId?: string;          // ID der ursprünglichen Rechnung (falls eine Revision)
    versionDate?: string;                // Datum dieser Version 
    versionReason?: string;              // Grund für die Erstellung dieser Version
  };
  
  // NEU: Draft-ID für automatische Löschung nach Rechnungserstellung
  selectedDraftId?: string | null;
  
  // NEU: Stornorechnungs-Verknüpfungen (Bidirektional)
  cancellationForInvoiceId?: string | null;  // ID der ursprünglichen Rechnung (nur bei Stornorechnungen)
  cancellationInvoiceNumber?: string | null; // Rechnungsnummer der Originalrechnung → BT-25 im XML
  cancelledByInvoiceId?: string | null;      // ID der Stornorechnung (nur bei stornierten Rechnungen)
  isCancelled?: boolean;                      // Flag: Wurde diese Rechnung storniert?
  isCancellation?: boolean;                   // Flag: Ist dies eine Stornorechnung?
  
  // NEU: Mehrsprachigkeit - Rechnungssprache
  documentLanguage?: string; // ISO 639-1 Code (de, en, fr, it, es) - Standard: 'de'

  // NEU: Abschlagszahlungen für Schlussrechnung (Header-Level)
  existingDownPayments?: Array<{
    invoiceNumber: string;
    invoiceDate: string;
    amount: number;
    netAmount?: number;
    taxAmount?: number;
  }>;
  
  // OPTIONAL: Übersetzungsinformationen für Debugging und Qualitätssicherung
  translationMetadata?: {
    sourceLanguage: string;           // Ursprungssprache (meist 'de')
    targetLanguage: string;           // Zielsprache der Übersetzung
    translatedFields: string[];       // Liste der übersetzten Felder
    translationTimestamp?: string;    // Zeitstempel der Übersetzung
    translationService?: string;      // Verwendeter Service (z.B. 'claude-3-5-sonnet')
  };
  
  // grandTotalAmount might be calculated backend-side, but could be here too
  // grandTotalAmount?: number;

  // NEU: Dokumentebene Rabatte/Aufschläge (BG-20/BG-21)
  documentAllowances?: DocumentAllowance[] | null;

  // NEU: Skontobedingungen
  skonto?: Skonto | null;

  // NEU: Email-Versand an Kunden nach Rechnungserstellung
  sendToEmail?: string | null;

  // VB-1: Rechnungsweiter Default für die Steuerart neuer Positionen (additiv, optional).
  defaultTaxCategorySelection?: InvoiceTaxCategoryKey | null;
}